import React, { useState, Component } from 'react';
import { motion } from 'framer-motion';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, LineChart, Line, ComposedChart, ScatterChart, Scatter, ZAxis } from 'recharts';
import { TrendingUp, TrendingDown, RefreshCw, Calendar, Activity, DollarSign, BarChart2, ArrowRight, Zap, Clock, AlertCircle } from 'lucide-react';
// --- Mock Data ---
const tradingData = Array.from({
  length: 24
}, (_, i) => {
  const hour = i;
  const volatility = 10 + Math.random() * 20 + (hour >= 16 && hour <= 19 ? 30 : 0);
  const price = 80 + Math.sin(hour / 3) * 20 + Math.random() * 10;
  const spread = Math.abs(price - (75 + Math.sin(hour / 3) * 15));
  const volume = 1000 + Math.random() * 500 + (hour >= 8 && hour <= 20 ? 1000 : 0);
  return {
    time: `${hour}:00`,
    price: Math.round(price),
    volatility: Math.round(volatility),
    spread: parseFloat(spread.toFixed(2)),
    volume: Math.round(volume),
    signal: spread > 15 ? 'Strong Buy' : spread > 10 ? 'Buy' : 'Hold'
  };
});
const orderBook = [{
  price: 105,
  volume: 150,
  type: 'ask'
}, {
  price: 104,
  volume: 220,
  type: 'ask'
}, {
  price: 103,
  volume: 340,
  type: 'ask'
}, {
  price: 102,
  volume: 580,
  type: 'ask'
}, {
  price: 101,
  volume: 890,
  type: 'ask'
}, {
  price: 100,
  volume: 1200,
  type: 'mid'
}, {
  price: 99,
  volume: 950,
  type: 'bid'
}, {
  price: 98,
  volume: 620,
  type: 'bid'
}, {
  price: 97,
  volume: 410,
  type: 'bid'
}, {
  price: 96,
  volume: 280,
  type: 'bid'
}, {
  price: 95,
  volume: 180,
  type: 'bid'
}];
const signals = [{
  id: 1,
  type: 'Arbitrage',
  pair: 'N2EX vs BritNed',
  profit: '€4.50/MWh',
  confidence: 'High',
  time: '14:00-16:00'
}, {
  id: 2,
  type: 'Volatility',
  pair: 'Peak Load',
  profit: '€12.20/MWh',
  confidence: 'Medium',
  time: '17:00-19:00'
}, {
  id: 3,
  type: 'Spread',
  pair: 'Day-Ahead vs Intraday',
  profit: '€2.80/MWh',
  confidence: 'High',
  time: 'Now'
}];
// --- Components ---
const CustomTooltip = ({
  active,
  payload,
  label
}: any) => {
  if (active && payload && payload.length) {
    return <div className="bg-slate-900/95 backdrop-blur-xl border border-purple-500/30 rounded-xl p-4 shadow-xl z-50 text-white" data-id="element-3440">
        <p className="text-xs font-bold text-purple-300 mb-2" data-id="element-3441">{label}</p>
        <div className="space-y-1" data-id="element-3442">
          {payload.map((entry: any) => <div key={entry.name} className="flex items-center justify-between gap-4" data-id="element-3443">
              <div className="flex items-center gap-2" data-id="element-3444">
                <div className="w-2 h-2 rounded-full" style={{
              backgroundColor: entry.color
            }} data-id="element-3445" />
                <span className="text-xs text-slate-300 capitalize" data-id="element-3446">
                  {entry.name}
                </span>
              </div>
              <span className="text-xs font-mono font-medium text-white" data-id="element-3447">
                {typeof entry.value === 'number' ? entry.value.toFixed(2) : entry.value}
                {entry.unit || ''}
              </span>
            </div>)}
        </div>
      </div>;
  }
  return null;
};
export function UKTradingDashboard() {
  return <div className="min-h-screen p-6 space-y-6 pb-20 bg-slate-50/50" data-id="element-3448">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-3449">
        <div data-id="element-3450">
          <div className="flex items-center gap-2 mb-1" data-id="element-3451">
            <div className="p-2 bg-purple-600 rounded-lg" data-id="element-3452">
              <TrendingUp size={20} className="text-white" data-id="element-3453" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight" data-id="element-3454">
              UK Trading Desk
            </h1>
          </div>
          <p className="text-slate-500 text-sm md:text-base ml-11" data-id="element-3455">
            Real-time spreads, volatility analysis, and trading signals
          </p>
        </div>

        <div className="flex items-center gap-3 ml-11 md:ml-0" data-id="element-3456">
          <div className="flex items-center gap-2 px-3 py-2 bg-purple-50 border border-purple-100 rounded-lg text-sm font-medium text-purple-700" data-id="element-3457">
            <Activity size={16} className="animate-pulse" data-id="element-3458" />
            <span data-id="element-3459">Market Active</span>
          </div>
          <button className="flex items-center gap-2 px-3 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors shadow-sm shadow-purple-500/20" data-id="element-3460">
            <RefreshCw size={16} data-id="element-3461" />
            <span data-id="element-3462">Refresh</span>
          </button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-3463">
        {[{
        title: 'N2EX Spread',
        value: '€12.40',
        unit: '/MWh',
        change: '+2.4%',
        trend: 'up',
        icon: ArrowRight,
        color: 'purple'
      }, {
        title: 'Volatility Index',
        value: '42.8',
        unit: 'VIX',
        change: '+5.1%',
        trend: 'up',
        icon: Activity,
        color: 'rose'
      }, {
        title: 'Market Depth',
        value: 'High',
        unit: '',
        change: '€1.2B Vol',
        trend: 'stable',
        icon: BarChart2,
        color: 'blue'
      }, {
        title: 'Spark Spread',
        value: '€8.20',
        unit: '/MWh',
        change: '-1.2%',
        trend: 'down',
        icon: Zap,
        color: 'amber'
      }].map((metric, idx) => <motion.div key={metric.title} initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: idx * 0.1
      }} className="bg-white/60 backdrop-blur-xl border border-slate-200/60 rounded-xl p-5 shadow-sm hover:shadow-md transition-all group" data-id="element-3464">
            <div className="flex justify-between items-start mb-4" data-id="element-3465">
              <div className={`p-2 rounded-lg bg-${metric.color}-50 text-${metric.color}-600 group-hover:bg-${metric.color}-100 transition-colors`} data-id="element-3466">
                <metric.icon size={20} data-id="element-3467" />
              </div>
              <div className={`flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full ${metric.trend === 'up' ? 'bg-emerald-50 text-emerald-700' : metric.trend === 'down' ? 'bg-rose-50 text-rose-700' : 'bg-blue-50 text-blue-700'}`} data-id="element-3468">
                {metric.trend === 'up' ? <TrendingUp size={12} data-id="element-3469" /> : <TrendingDown size={12} data-id="element-3470" />}
                {metric.change}
              </div>
            </div>
            <div data-id="element-3471">
              <p className="text-sm text-slate-500 font-medium" data-id="element-3472">
                {metric.title}
              </p>
              <div className="flex items-baseline gap-1 mt-1" data-id="element-3473">
                <span className="text-2xl font-bold text-slate-900" data-id="element-3474">
                  {metric.value}
                </span>
                <span className="text-sm text-slate-400 font-medium" data-id="element-3475">
                  {metric.unit}
                </span>
              </div>
            </div>
          </motion.div>)}
      </div>

      {/* Main Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-3476">
        {/* Spread Analysis */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.4
      }} className="lg:col-span-2 bg-slate-900 rounded-xl p-6 shadow-lg text-white" data-id="element-3477">
          <div className="flex items-center justify-between mb-6" data-id="element-3478">
            <div data-id="element-3479">
              <h3 className="text-lg font-bold text-white" data-id="element-3480">Spread Analysis</h3>
              <p className="text-sm text-slate-400" data-id="element-3481">
                Interconnector price differentials
              </p>
            </div>
            <div className="flex gap-2" data-id="element-3482">
              <span className="px-2 py-1 bg-purple-500/20 rounded text-xs font-medium text-purple-300 border border-purple-500/30" data-id="element-3483">
                High Volatility Detected
              </span>
            </div>
          </div>

          <div className="h-[350px] w-full" data-id="element-3484">
            <ResponsiveContainer width="100%" height="100%" data-id="element-3485">
              <ComposedChart data={tradingData} margin={{
              top: 10,
              right: 30,
              left: 0,
              bottom: 0
            }} data-id="element-3486">
                <defs data-id="element-3487">
                  <linearGradient id="colorSpread" x1="0" y1="0" x2="0" y2="1" data-id="element-3488">
                    <stop offset="5%" stopColor="#a855f7" stopOpacity={0.3} data-id="element-3489" />
                    <stop offset="95%" stopColor="#a855f7" stopOpacity={0} data-id="element-3490" />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#334155" data-id="element-3491" />
                <XAxis dataKey="time" axisLine={false} tickLine={false} tick={{
                fontSize: 12,
                fill: '#94a3b8'
              }} dy={10} data-id="element-3492" />
                <YAxis yAxisId="left" orientation="left" axisLine={false} tickLine={false} tick={{
                fontSize: 12,
                fill: '#94a3b8'
              }} label={{
                value: 'Spread (€)',
                angle: -90,
                position: 'insideLeft',
                style: {
                  fill: '#94a3b8',
                  fontSize: 12
                }
              }} data-id="element-3493" />
                <YAxis yAxisId="right" orientation="right" axisLine={false} tickLine={false} tick={{
                fontSize: 12,
                fill: '#94a3b8'
              }} label={{
                value: 'Volatility',
                angle: 90,
                position: 'insideRight',
                style: {
                  fill: '#94a3b8',
                  fontSize: 12
                }
              }} data-id="element-3494" />
                <Tooltip content={<CustomTooltip data-id="element-3496" />} data-id="element-3495" />
                <Area yAxisId="left" type="monotone" dataKey="spread" name="Price Spread" stroke="#a855f7" fill="url(#colorSpread)" strokeWidth={2} data-id="element-3497" />
                <Line yAxisId="right" type="monotone" dataKey="volatility" name="Volatility Index" stroke="#f43f5e" strokeWidth={2} dot={false} data-id="element-3498" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        {/* Active Signals */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.5
      }} className="bg-white/60 backdrop-blur-xl border border-slate-200/60 rounded-xl p-6 shadow-sm flex flex-col" data-id="element-3499">
          <div className="mb-6" data-id="element-3500">
            <h3 className="text-lg font-bold text-slate-900" data-id="element-3501">Active Signals</h3>
            <p className="text-sm text-slate-500" data-id="element-3502">AI-detected opportunities</p>
          </div>

          <div className="flex-1 space-y-4" data-id="element-3503">
            {signals.map(signal => <div key={signal.id} className="p-4 bg-white rounded-lg border border-slate-100 shadow-sm hover:shadow-md transition-all cursor-pointer group" data-id="element-3504">
                <div className="flex justify-between items-start mb-2" data-id="element-3505">
                  <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${signal.confidence === 'High' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`} data-id="element-3506">
                    {signal.confidence} Conf.
                  </span>
                  <span className="text-xs text-slate-400 flex items-center gap-1" data-id="element-3507">
                    <Clock size={12} data-id="element-3508" />
                    {signal.time}
                  </span>
                </div>
                <h4 className="font-bold text-slate-900 mb-1" data-id="element-3509">{signal.type}</h4>
                <p className="text-sm text-slate-600 mb-3" data-id="element-3510">{signal.pair}</p>
                <div className="flex justify-between items-center pt-3 border-t border-slate-50" data-id="element-3511">
                  <span className="text-xs text-slate-500" data-id="element-3512">Est. Profit</span>
                  <span className="font-bold text-purple-600 group-hover:scale-105 transition-transform" data-id="element-3513">
                    {signal.profit}
                  </span>
                </div>
              </div>)}
          </div>
        </motion.div>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3514">
        {/* Order Book Depth */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.6
      }} className="bg-white/60 backdrop-blur-xl border border-slate-200/60 rounded-xl p-6 shadow-sm" data-id="element-3515">
          <div className="mb-6 flex justify-between items-center" data-id="element-3516">
            <div data-id="element-3517">
              <h3 className="text-lg font-bold text-slate-900" data-id="element-3518">Market Depth</h3>
              <p className="text-sm text-slate-500" data-id="element-3519">Order book visualization</p>
            </div>
            <div className="flex items-center gap-2 text-xs font-medium" data-id="element-3520">
              <span className="flex items-center gap-1 text-rose-600" data-id="element-3521">
                <div className="w-2 h-2 rounded-full bg-rose-500" data-id="element-3522" /> Ask
              </span>
              <span className="flex items-center gap-1 text-emerald-600" data-id="element-3523">
                <div className="w-2 h-2 rounded-full bg-emerald-500" data-id="element-3524" /> Bid
              </span>
            </div>
          </div>

          <div className="h-[250px] w-full" data-id="element-3525">
            <ResponsiveContainer width="100%" height="100%" data-id="element-3526">
              <BarChart data={orderBook} margin={{
              top: 10,
              right: 10,
              left: -20,
              bottom: 0
            }} data-id="element-3527">
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" data-id="element-3528" />
                <XAxis dataKey="price" axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#64748b'
              }} data-id="element-3529" />
                <YAxis axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#64748b'
              }} data-id="element-3530" />
                <Tooltip content={<CustomTooltip data-id="element-3532" />} data-id="element-3531" />
                <Bar dataKey="volume" name="Volume" data-id="element-3533">
                  {orderBook.map((entry, index) => <cell key={`cell-${index}`} fill={entry.type === 'ask' ? '#f43f5e' : entry.type === 'bid' ? '#10b981' : '#64748b'} data-id="element-3534" />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        {/* Volume Analysis */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.7
      }} className="bg-white/60 backdrop-blur-xl border border-slate-200/60 rounded-xl p-6 shadow-sm" data-id="element-3535">
          <div className="mb-6" data-id="element-3536">
            <h3 className="text-lg font-bold text-slate-900" data-id="element-3537">Volume Profile</h3>
            <p className="text-sm text-slate-500" data-id="element-3538">Traded volume by hour</p>
          </div>

          <div className="h-[250px] w-full" data-id="element-3539">
            <ResponsiveContainer width="100%" height="100%" data-id="element-3540">
              <BarChart data={tradingData} margin={{
              top: 10,
              right: 10,
              left: -20,
              bottom: 0
            }} data-id="element-3541">
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" data-id="element-3542" />
                <XAxis dataKey="time" axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#64748b'
              }} interval={3} data-id="element-3543" />
                <YAxis axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#64748b'
              }} data-id="element-3544" />
                <Tooltip content={<CustomTooltip data-id="element-3546" />} data-id="element-3545" />
                <Bar dataKey="volume" name="Volume" fill="#8b5cf6" radius={[2, 2, 0, 0]} data-id="element-3547" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </motion.div>
      </div>
    </div>;
}